import { useQuery } from "@tanstack/react-query";
import { CountryCode } from "@vygruppen/vy-train-map";
import { getBackendUrl } from "api/common";
import { queryFnGET } from "api/tanStackQuery/helpers";
import { queryClient } from "api/tanStackQuery/queryClient";
import { useQueryAggregatedWithInternalMessages } from "features/CenterContent/RoleContent/InternalMessage/utils/withInternalMessages";
import { TrainTab } from "features/CenterContent/shared/Tabs/useTabsContext";
import {
  TrainInformationAggregated,
  TrainInformationAggregatedResponse,
} from "features/CenterContent/VehicleDetails/TrainDetails/TrainCondition/OperationalTrainInfo/types/trainInformationAggregated";
import { useCallback, useMemo } from "react";
import { useParams } from "react-router-dom";
import { useWebsocket } from "websocket/useWebsocket";
import {
  BackendIdentifier,
  TrainInformationMessage,
  WebsocketTopic,
} from "websocket/websocketSharedWorkerTypes";

const getNewState = (
  message: TrainInformationAggregated,
  prev: TrainInformationAggregated[] | undefined,
): TrainInformationAggregated[] => {
  // No previous messages
  if (!prev?.length) {
    return [message];
  }

  // Check if trainInfoMessage is a later version of an already existing trainInfo.
  const latestMatch = prev.find(
    (info) => info.latestInfoUuid === message.latestInfoUuid,
  );

  // If there is a match, only add the new version
  if (latestMatch) {
    const unchangedLatestMatchVersions = latestMatch.versions.filter(
      (oldVersion) =>
        !message.versions.some(
          (newVersion) => newVersion.version === oldVersion.version,
        ),
    );

    return [
      ...prev.filter((info) => info.latestInfoUuid !== message.latestInfoUuid),
      {
        latestInfoUuid: latestMatch.latestInfoUuid,
        versions: [...message.versions, ...unchangedLatestMatchVersions],
      },
    ];
  }

  // Check if trainInfoMessage replaces an already existing trainInfo.
  const replacedMatch = prev.find(
    (prevInfo) => prevInfo.latestInfoUuid === message.latestReplacedUuid,
  );

  // else, if the new version replaces an existing trainInfo, add the new version to the replaced trainInfo
  if (replacedMatch) {
    const unchangedReplacedMatchVersions = replacedMatch.versions.filter(
      (oldVersion) =>
        !message.versions.some(
          (newVersion) => newVersion.version === oldVersion.version,
        ),
    );

    return [
      ...prev.filter(
        (prevInfo) => prevInfo.latestInfoUuid !== message.latestReplacedUuid,
      ),
      {
        latestInfoUuid: message.latestInfoUuid,
        versions: [...message.versions, ...unchangedReplacedMatchVersions],
      },
    ];
  }

  // else, add as a separate and new trainInformation
  return [...prev, message];
};

export const useTrainInformation = (
  trainNumber: string | undefined,
  trainDate: string | undefined,
  countryCode: CountryCode | undefined,
) => {
  const queryKey = useMemo(
    () => ["trainInformation", countryCode, trainDate, trainNumber],
    [countryCode, trainDate, trainNumber],
  );

  const query = useQueryAggregatedWithInternalMessages(
    useQuery<TrainInformationAggregated[]>({
      queryKey,
      enabled: Boolean(countryCode && trainDate && countryCode),
      queryFn: ({ signal }) =>
        queryFnGET<TrainInformationAggregatedResponse>({
          signal,
          url: `${getBackendUrl()}/trainInformation/train/${countryCode}/${trainNumber}/${trainDate}/timeline`,
        }).then((res) => res.trainInformation),
      refetchOnWindowFocus: true,
    }),
  );

  const messageHandler = useCallback(
    ({ message }: TrainInformationMessage) => {
      if (trainNumber && trainDate) {
        // Only update if the information is about the train currently viewed by the user
        if (
          message.versions.some(
            ({ trainIdentifier }) =>
              trainIdentifier.identifier === trainNumber &&
              trainIdentifier.nominalDate === trainDate,
          )
        ) {
          queryClient.setQueryData<TrainInformationAggregated[]>(
            queryKey,
            (prev) => getNewState(message, prev),
          );
        }
      }
    },
    [trainNumber, trainDate, queryKey],
  );

  useWebsocket(BackendIdentifier.DROPS_BACKEND, {
    [WebsocketTopic.TRAIN_INFORMATION]: messageHandler,
  });

  return query;
};

export const useTrainInformationFromURLParams = () => {
  const { trainNumber, trainDate, countryCode } =
    useParams<Partial<TrainTab>>();

  return useTrainInformation(trainNumber, trainDate, countryCode);
};
